/** @format */

import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { LuPencil, LuTrash2 } from "react-icons/lu";
import { RiSteering2Line } from "react-icons/ri";
import QuoteStep from "../enums/QuoteStep";
import FormNavigationButtons from "../../../shared/components/FormNavigationButtons";
import TqAddDriverMapper from "../mappers/tqAddDriverMapper";
import QuoteFormWrapper from "../../../shared/components/quoteFormWrapper";
import TqAddDriverModal from "../components/tqAddDriverModal";
import useQuoteStore from "../store/QuoteStore";
import { saveQuote, fetchPanelData } from "../services/quoteService";
import { fetchPolicyDetails } from "../services/quoteService";
import {
  deformatDateT,
  deformatMonthT,
  deformatYearT,
} from "../../../shared/utils/formatters";
import { handleNext } from "../../../shared/utils/navigationUtils";
import useAppStateStore from "../../../shared/store/AppStateStore";
import { useNavigate } from "fusion-plugin-react-router";
import { downloadTOBA } from "../../../shared/utils/apiUtils";
import { HotjarEventNames } from "../../../shared/utils/hotjarEventNames";
import { TrackHotjarEvent } from "../../../shared/utils/trackHotjarEvent";
import FieldStack from "../../../shared/components/FieldStack";
import QuoteStepHeading from "../../../shared/components/QuoteStepHeading";
import RadioGroup from "../../../shared/components/RadioGroup";
import Button from "../../../shared/components/Button";

const TqAddDriver = () => {
  const STATE = useAppStateStore();
  const claims = STATE.claims;
  const additionalDriver = STATE.additionalDriver;
  const setAdditionalDriver = STATE.setAdditionalDriver;

  const methods = useForm({
    mode: "onChange",
    defaultValues: { onlyDriver: additionalDriver },
  });
  const { setValue, register, trigger } = methods;
  const navigate = useNavigate();

  const [drivers, setDrivers] = useState([]);
  const [driverModalOpen, setDriverModalOpen] = useState(false);
  const [localClaimFields, setLocalClaimFields] = useState([]);
  const [showDriverError, setShowDriverError] = useState(false);

  const maxDriversAllowed = 4;

  const fetchDrivers = () => {
    const storeDrivers = TqAddDriverMapper.get();
    setDrivers(storeDrivers);
  };

  useEffect(() => {
    fetchDrivers();
  }, []);

  const handleDelete = (index) => {
    TqAddDriverMapper.delete(index);
    fetchDrivers();
  };

  const handleDriverEdit = (driver, index) => {
    setDriverModalOpen(true);
    setValue("driverIndex", index);

    // Set form values based on driver data
    setValue("addDriverTitle", {
      value: driver.titleId,
      label: driver.titleDescription,
    });
    setValue("addDriver_firstName", driver.forename);
    setValue("addDriver_lastName", driver.surname);
    setValue("addDob_Date", deformatDateT(driver.dateOfBirth));
    setValue("addlicense", driver.licenceNumber?.slice(0, 11));
    setValue("addlicenseUser", driver.licenceNumber?.slice(11));
    setValue("rating", {
      value: driver.uberDriverRatingId,
      label: driver.uberDriverRatingDescription,
    });
    setValue("relation", {
      value: driver.relationshipId,
      label: driver.relationshipDescription,
    });
    setValue("martialStatus", {
      value: driver.maritalStatusId,
      label: driver.maritalStatusDescription,
    });
    setValue(
      "AddDriverbatch_Month.value",
      deformatMonthT(driver.qualifications[0].datePassed),
    );
    setValue(
      "AddDriverbatch_Year.value",
      deformatYearT(driver.qualifications[0].datePassed),
    );
    setValue("residecy", driver.residentSinceBirth ? "Yes" : "No");
    setValue("residencyYears", driver.ukResidenceYears);
    setValue("addvehicleUse", driver.vehicleUseId);

    // Set occupation details
    if (driver.occupations?.[0]) {
      setValue("occupation", {
        value: driver.occupations[0].occupationId,
      });
      setValue("addDriverempStatus", {
        value: driver.occupations[0].employmentStatusId,
      });
      setValue("addDriverBusinessOccupation", {
        value: driver.occupations[0].employersBusinessId,
      });
    }
    if (driver.occupations?.[1]) {
      setValue("parttime", "Yes");
      setValue("addDriverpartTimeOcc", {
        value: driver.occupations[1].occupationId,
      });
      setValue("addDriverPartTimeEmpStatus", {
        value: driver.occupations[1].employmentStatusId,
      });
      setValue("addDriverpartTimeBusinessOcc", {
        value: driver.occupations[1].employersBusinessId,
      });
    } else {
      setValue("parttime", "No");
    }
    // Set claims if they exist
    if (driver.claims && driver.claims.length > 0) {
      setValue("AdditionalClaim", "Yes");
      const formattedClaims = driver.claims.map((claim) => ({
        id: claim.claimDetailsId,
        typeId: claim.typeId,
        claimDate: claim.claimDate,
        cost: claim.cost,
        injuryInvolved: claim.injuryInvolved,
        atFault: claim.atFault,
        objectId: claim.objectId || null,
        claimDetailsId: claim.claimDetailsId || null,
        theftFromHome: claim.theftFromHome || false,
        statusId: claim.statusId || null,
        ncdAffected: claim.ncdAffected || false,
        incurredOnPolicy: claim.incurredOnPolicy || false,
        referenceNumber: claim.referenceNumber || null,
        parentObjectId: claim.parentObjectId || null,
        parentObjectHistoryId: claim.parentObjectHistoryId || 0,
      }));

      setLocalClaimFields(formattedClaims);
    } else {
      setValue("AdditionalClaim", "No");
      setLocalClaimFields([]);
    }
  };

  const handleAddDriver = () => {
    methods.reset({
      driverIndex: null,
      addDriverTitle: null,
      addDriver_firstName: "",
      addDriver_lastName: "",
      addDob_Date: null,
      addlicense: "",
      addlicenseUser: "",
      rating: null,
      relation: null,
      occupation: null,
      addDriverempStatus: null,
      addDriverBusinessOccupation: null,
      addDriverpartTimeOcc: null,
      addDriverPartTimeEmpStatus: null,
      addDriverpartTimeBusinessOcc: null,
      parttime: null,
      residecy: null,
      residencyYears: null,
      martialStatus: null,
      AddDriverbatch_Month: null,
      AddDriverbatch_Year: null,
      addvehicleUse: null,
      AdditionalClaim: null,
      additionalDriverClaims: [],
    });

    if (drivers.length < maxDriversAllowed) {
      setDriverModalOpen(true);
    }
  };

  useEffect(() => {
    if (driverModalOpen) {
      document.body.classList.add("no-scroll");
    } else {
      document.body.classList.remove("no-scroll");
    }

    return () => {
      document.body.classList.remove("no-scroll");
    };
  }, [driverModalOpen]);

  useEffect(() => {
    TrackHotjarEvent(HotjarEventNames.TAXI_QUOTE_TQADDDRIVER_ENTERED);
  }, []);

  const [onlyDriver, setOnlyDriver] = useState(additionalDriver);

  useEffect(() => {
    setOnlyDriver(additionalDriver);
    setValue("onlyDriver", additionalDriver);
  }, [additionalDriver, setValue]);

  useEffect(() => {
    if (drivers.length > 0 || onlyDriver === "No") {
      setShowDriverError(false);
    }
  }, [drivers.length, onlyDriver]);

  const onClick = () => {
    TrackHotjarEvent(HotjarEventNames.TAXI_QUOTE_TQADDDRIVER_NEXT_CLICKED);
    setAdditionalDriver(onlyDriver);
    const quoteData = useQuoteStore.getState().quote;
    saveQuote(quoteData, navigate).then((data) => {
      fetchPolicyDetails().catch((err) => {
        console.error("Policy details fetch failed:", err);
      });
      fetchPanelData(navigate, null, data.risk.riskObjectId).then(() => {
        handleNext(QuoteStep.TQ_PANEL);
      });
    });
    handleNext(QuoteStep.TQ_PANEL_LOADER);
  };

  const onInvalidClick = () => {
    TrackHotjarEvent(HotjarEventNames.TAXI_QUOTE_TQADDDRIVER_NEXT_CLICKED);
    setShowDriverError(true);
  };

  return (
    <FormProvider {...methods}>
      <QuoteFormWrapper>
        <QuoteStepHeading
          title="Additional Drivers"
          description={
            <>
              It is your duty to make a fair representation of the risk. This
              means you must describe your business accurately and disclose
              every material fact or circumstance known to you or to anyone who
              is responsible for your insurance. Please see our{" "}
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  downloadTOBA({ navigate });
                }}
                className="tw-cursor-pointer tw-bg-transparent tw-p-0 tw-text-dis-deep-blue tw-underline hover:tw-bg-transparent"
              >
                Terms of Business
              </a>{" "}
              for further information. Please ensure you have permission from
              the driver to use their information.
            </>
          }
        />

        <FieldStack className="tw-text-sm">
          <label className="tw-mb-2 tw-block tw-text-base tw-font-bold tw-text-dis-navy">
            Do you need to add an additional driver who needs to be included on
            the policy?
          </label>
          <div className="tw-text-sm">
            <div className="tw-flex tw-flex-col">
              {drivers.length > 0 &&
                drivers.map((driver, index) => (
                  <div
                    className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-border-0 tw-border-t tw-border-solid tw-border-dis-divider tw-py-3"
                    key={index}
                  >
                    <div className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-3">
                      <RiSteering2Line
                        className="tw-h-6 tw-w-6 tw-shrink-0 tw-text-dis-navy"
                        aria-hidden="true"
                      />
                      <span className="tw-min-w-0 tw-text-[17px] tw-font-semibold tw-text-dis-navy">
                        {driver.forename} {driver.surname}
                      </span>
                    </div>
                    <div className="tw-flex">
                      <button
                        type="button"
                        onClick={() => handleDriverEdit(driver, index)}
                        className="tw-mr-1.5 tw-flex tw-h-[42px] tw-w-[42px] tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-dis-field-hover tw-text-dis-navy hover:tw-bg-dis-sky-tint focus-visible:tw-outline-2 focus-visible:tw-outline-dis-deep-blue focus-visible:tw-outline-offset-2"
                        aria-label={`Edit ${driver.forename} ${driver.surname}`}
                      >
                        <LuPencil
                          className="tw-h-5 tw-w-5"
                          strokeWidth={2.2}
                          aria-hidden="true"
                        />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(index)}
                        className="tw-flex tw-h-[42px] tw-w-[42px] tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-dis-field-hover tw-text-dis-navy hover:tw-bg-dis-sky-tint focus-visible:tw-outline-2 focus-visible:tw-outline-dis-deep-blue focus-visible:tw-outline-offset-2"
                        aria-label={`Delete ${driver.forename} ${driver.surname}`}
                      >
                        <LuTrash2
                          className="tw-h-5 tw-w-5"
                          strokeWidth={2.2}
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                  </div>
                ))}
              {drivers.length < maxDriversAllowed && (
                <div className="tw-mt-3 tw-mb-5 tw-flex">
                  <Button
                    type="button"
                    variant="soft"
                    size="small"
                    onClick={handleAddDriver}
                    disabled={onlyDriver === "No"}
                  >
                    Add additional driver
                  </Button>
                </div>
              )}

              {drivers.length === maxDriversAllowed && (
                <div
                  className="tw-mt-3 tw-border-0 tw-border-l-4 tw-border-solid tw-border-dis-error tw-py-2 tw-pl-3 tw-text-base tw-font-bold tw-text-dis-error"
                  role="alert"
                >
                  You’ve reached the maximum of 4 additional drivers.
                </div>
              )}
            </div>
            {drivers.length < 1 && showDriverError && (
              <p
                className="tw-m-0 tw-border-0 tw-border-l-4 tw-border-solid tw-border-l-dis-error tw-bg-dis-error-bg tw-p-3 tw-text-sm tw-text-dis-error"
                role="alert"
              >
                Please add an additional driver, or confirm you are the only
                driver to continue.
              </p>
            )}
            {drivers.length < 1 && (
              <>
                <p className="tw-mt-4 tw-mb-1 tw-text-dis-small tw-leading-[1.45] tw-text-dis-text-muted">
                  If you do not need to add another driver, select Yes below.
                </p>
                <RadioGroup
                  name="onlyDriver"
                  mainLabel="Are you the only driver to be insured on the vehicle?"
                  register={register}
                  required={false}
                  trigger={trigger}
                  yesValue="No"
                  noValue="Yes"
                  onYes={() => setOnlyDriver("No")}
                  onNo={() => setOnlyDriver("Yes")}
                />
              </>
            )}
          </div>
        </FieldStack>
      </QuoteFormWrapper>
      <FormNavigationButtons
        type="button"
        prevStep={claims === "Yes" ? QuoteStep.TQ_CLAIM : QuoteStep.TQ6}
        onClick={onClick}
        onInvalidClick={onInvalidClick}
        isValid={drivers.length > 0 || onlyDriver === "No"}
      />

      <TqAddDriverModal
        onDriverSaved={fetchDrivers}
        isModalOpen={driverModalOpen}
        setIsModalOpen={setDriverModalOpen}
        localClaimFields={localClaimFields}
        setLocalClaimFields={setLocalClaimFields}
      />
    </FormProvider>
  );
};

export default TqAddDriver;
